import { useEffect, useRef, useState } from 'react'
import AgoraRTC, {
  IMicrophoneAudioTrack,
  ICameraVideoTrack,
} from 'agora-rtc-sdk-ng'
import VirtualBackgroundExtension from 'agora-extension-virtual-background'
import { IVirtualBackgroundProcessor } from 'agora-extension-virtual-background'
import { removeLocalStorage } from 'utils/localStorage'

interface NetworkInformation {
  effectiveType?: string
  downlink?: number
  rtt?: number
  saveData?: boolean
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation
  mozConnection?: NetworkInformation
  webkitConnection?: NetworkInformation
}

type DeviceList = {
  cameras: MediaDeviceInfo[]
  microphones: MediaDeviceInfo[]
}

type NetworkQuality = {
  txQuality: number
  rxQuality: number
  overallQuality: number
  timestamp?: number
  browserQuality?: {
    effectiveType: string
    downlink: number
    rtt: number
    saveData: boolean
  }
}

type ConnectionStatus =
  | 'excellent'
  | 'good'
  | 'poor'
  | 'critical'
  | 'disconnected'

type VideoQuality = 'low' | 'medium' | 'high'

type VideoQualityConfig = {
  width: number
  height: number
  frameRate: number
  bitrateMax: number
  bitrateMin: number
}

const VIDEO_QUALITY_CONFIGS: Record<VideoQuality, VideoQualityConfig> = {
  low: {
    width: 640,
    height: 360,
    frameRate: 15,
    bitrateMax: 400,
    bitrateMin: 200,
  },
  medium: {
    width: 960,
    height: 540,
    frameRate: 24,
    bitrateMax: 1200,
    bitrateMin: 600,
  },
  high: {
    width: 1280,
    height: 720,
    frameRate: 30,
    bitrateMax: 2000,
    bitrateMin: 1000,
  },
}

export default function useVideocall() {
  const [processor, setProcessor] =
    useState<IVirtualBackgroundProcessor | null>(null)
  const [isLocalMain, setIsLocalMain] = useState(false)
  const [localPaused, setLocalPaused] = useState(false)
  const [showConfig, setShowConfig] = useState(false)
  const [buttonsDisabled, setButtonsDisabled] = useState(false)
  const [showDeviceConfig, setShowDeviceConfig] = useState(false)
  const [selectedBg, setSelectedBg] = useState<string>('none')
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [remotePaused, setRemotePaused] = useState(false)
  const [remoteDisconnected, setRemoteDisconnected] = useState(true)
  const [remoteUsers, setRemoteUsers] = useState<
    Map<
      string | number,
      {
        uid: string | number
        videoTrack: any
        audioTrack: any
        videoPaused: boolean
        hasVideo: boolean
        hasAudio: boolean
      }
    >
  >(new Map())
  const [devices, setDevices] = useState<DeviceList>({
    cameras: [],
    microphones: [],
  })
  const [selectedCamera, setSelectedCamera] = useState<string>('')
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('')
  const [currentVideoQuality, setCurrentVideoQuality] =
    useState<VideoQuality>('high')
  const [autoQualityEnabled, setAutoQualityEnabled] = useState(true)
  const [wasVideoAutoPaused, setWasVideoAutoPaused] = useState(false)
  const [networkQuality, setNetworkQuality] = useState<NetworkQuality>({
    txQuality: 0,
    rxQuality: 0,
    overallQuality: 0,
  })
  const [networkMessage, setNetworkMessage] = useState<string>('')
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>('disconnected')
  const [connectionHistory, setConnectionHistory] = useState<NetworkQuality[]>(
    []
  )
  const [isMonitoring, setIsMonitoring] = useState(false)
  const [, setIsJoining] = useState(false)
  const joinInFlightRef = useRef(false)
  const initInFlightRef = useRef(false)
  const [localParameters, setLocalParameters] = useState<{
    audioTrack: IMicrophoneAudioTrack | null
    videoTrack: ICameraVideoTrack | null
    audioMuted: boolean
    videoEnabled: boolean
  }>({
    audioTrack: null,
    videoTrack: null,
    audioMuted: false,
    videoEnabled: true,
  })

  const agoraClient = AgoraRTC.createClient({
    mode: 'rtc',
    codec: 'vp8',
    role: 'host',
  })

  const extension = new VirtualBackgroundExtension()
  AgoraRTC.registerExtensions([extension])

  const getBrowserNetworkQuality = () => {
    try {
      const nav = navigator as unknown as NavigatorWithConnection
      const connection =
        nav.connection || nav.mozConnection || nav.webkitConnection

      if (connection) {
        return {
          effectiveType: connection.effectiveType || 'unknown',
          downlink: connection.downlink || 0,
          rtt: connection.rtt || 0,
          saveData: connection.saveData || false,
        }
      }
    } catch (error) {
      console.warn('Erro ao obter qualidade de rede do navegador:', error)
    }
    return undefined
  }

  const calculateStatusFromAgora = (
    txQuality: number,
    rxQuality: number,
    overallQuality: number
  ): ConnectionStatus => {
    const maxQuality = Math.max(txQuality, rxQuality)

    if (
      txQuality >= 6 ||
      rxQuality >= 6 ||
      (txQuality >= 5 && rxQuality >= 5)
    ) {
      return 'disconnected'
    } else if (maxQuality >= 4 || overallQuality >= 4) {
      return 'critical'
    } else if (maxQuality === 3 || overallQuality === 3) {
      return 'poor'
    } else if (maxQuality === 2 || overallQuality === 2) {
      return 'good'
    } else if (maxQuality <= 1 && overallQuality <= 1) {
      return 'excellent'
    }
    return 'good'
  }

  const checkInternetConnection = (): boolean => {
    if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
      return navigator.onLine
    }

    return true
  }

  const calculateStatusFromBrowser = (browserQuality: {
    effectiveType: string
    downlink: number
    rtt: number
  }): ConnectionStatus => {
    if (!checkInternetConnection()) {
      return 'disconnected'
    }
    const { effectiveType, downlink, rtt } = browserQuality

    if (
      effectiveType === 'slow-2g' ||
      effectiveType === '2g' ||
      (downlink < 0.5 && rtt > 2000)
    ) {
      return 'critical'
    }
    if (effectiveType === '3g' || (downlink < 1.5 && rtt > 1000)) {
      return 'poor'
    }
    if (effectiveType === '4g' && downlink >= 1.5 && rtt < 1000) {
      return 'good'
    }
    if (effectiveType === '4g' && downlink >= 10 && rtt < 100) {
      return 'excellent'
    }

    return 'good'
  }

  const combineNetworkQuality = (
    agoraQuality: {
      txQuality: number
      rxQuality: number
      overallQuality: number
    },
    browserQuality?: {
      effectiveType: string
      downlink: number
      rtt: number
      saveData: boolean
    }
  ): ConnectionStatus => {
    const agoraStatus = calculateStatusFromAgora(
      agoraQuality.txQuality,
      agoraQuality.rxQuality,
      agoraQuality.overallQuality
    )

    if (!browserQuality) {
      return agoraStatus
    }

    const browserStatus = calculateStatusFromBrowser(browserQuality)

    if (browserStatus === 'critical' || browserStatus === 'disconnected') {
      if (
        agoraStatus === 'critical' ||
        agoraStatus === 'disconnected' ||
        agoraStatus === 'poor'
      ) {
        return browserStatus
      }
      console.warn(
        'Navegador detectou conexão ruim antes do Agora. Monitorando...'
      )
      return browserStatus
    }

    if (browserStatus === 'poor') {
      if (agoraStatus === 'critical' || agoraStatus === 'disconnected') {
        return agoraStatus
      }
      return browserStatus
    }

    if (browserStatus === 'good' || browserStatus === 'excellent') {
      if (agoraStatus === 'critical' || agoraStatus === 'disconnected') {
        console.warn(
          'Navegador indica boa conexão, mas Agora detectou problema na chamada'
        )
        return agoraStatus
      }
      if (agoraStatus === 'excellent' && browserStatus === 'good') {
        return 'excellent'
      }
      return browserStatus
    }

    return agoraStatus
  }

  const renderRemoteVideos = (
    usersMap: Map<
      string | number,
      {
        uid: string | number
        videoTrack: any
        audioTrack: any
        videoPaused: boolean
        hasVideo: boolean
        hasAudio: boolean
      }
    >
  ) => {
    const remote = document.getElementById('remote')
    if (!remote) return

    const allVideos = remote.querySelectorAll('video')
    allVideos.forEach((video) => {
      if (video instanceof HTMLVideoElement) {
        video.pause()
        video.srcObject = null
      }
      video.remove()
    })

    const existingWrappers = remote.querySelectorAll('.video-wrapper')
    existingWrappers.forEach((wrapper) => {
      wrapper.remove()
    })

    const existingGrid = remote.querySelector('.remote-videos-grid')
    if (existingGrid) {
      existingGrid.remove()
    }

    const usersArray = Array.from(usersMap.values())

    if (usersArray.length === 0) {
      return
    }

    const usersWithVideo = usersArray.filter(
      (u) => u.hasVideo && u.videoTrack && !u.videoPaused
    )

    const usersWithPausedVideo = usersArray.filter(
      (u) => !u.hasVideo || u.videoPaused
    )

    let gridContainer = remote.querySelector(
      '.remote-videos-grid'
    ) as HTMLDivElement
    if (!gridContainer) {
      gridContainer = document.createElement('div')
      gridContainer.className = 'remote-videos-grid'
      gridContainer.style.display = 'grid'
      gridContainer.style.width = '100%'
      gridContainer.style.height = '100%'
      gridContainer.style.gap = '8px'
      gridContainer.style.padding = '8px'
      remote.appendChild(gridContainer)
    }

    const totalUsers = usersArray.length
    let cols = 1
    let rows = 1

    if (totalUsers === 1) {
      cols = 1
      rows = 1
    } else if (totalUsers === 2) {
      cols = 2
      rows = 1
    } else if (totalUsers === 3) {
      cols = 2
      rows = 2
    } else if (totalUsers === 4) {
      cols = 2
      rows = 2
    } else if (totalUsers <= 6) {
      cols = 3
      rows = 2
    } else if (totalUsers <= 9) {
      cols = 3
      rows = 3
    } else if (totalUsers <= 12) {
      cols = 4
      rows = 3
    } else if (totalUsers <= 16) {
      cols = 4
      rows = 4
    } else if (totalUsers <= 20) {
      cols = 5
      rows = 4
    } else {
      cols = 5
      rows = Math.ceil(totalUsers / 5)
    }

    gridContainer.style.gridTemplateColumns = `repeat(${cols}, 1fr)`
    gridContainer.style.gridTemplateRows = `repeat(${rows}, 1fr)`
    gridContainer.style.placeItems = 'stretch'
    gridContainer.style.width = '100%'
    gridContainer.style.height = '100%'
    gridContainer.style.margin = '0'
    gridContainer.style.padding = '8px'
    gridContainer.style.boxSizing = 'border-box'

    const existingWrappersInGrid =
      gridContainer.querySelectorAll('.video-wrapper')
    existingWrappersInGrid.forEach((wrapper) => {
      const video = wrapper.querySelector('video')
      if (video instanceof HTMLVideoElement) {
        video.pause()
        video.srcObject = null
      }
      wrapper.remove()
    })

    usersWithVideo.forEach((user) => {
      const videoWrapper = document.createElement('div')
      videoWrapper.className = 'video-wrapper'
      videoWrapper.setAttribute('data-user-uid', String(user.uid))
      videoWrapper.style.width = '100%'
      videoWrapper.style.height = '100%'
      videoWrapper.style.position = 'relative'
      videoWrapper.style.backgroundColor = '#000'
      videoWrapper.style.borderRadius = '8px'
      videoWrapper.style.overflow = 'hidden'
      videoWrapper.style.display = 'flex'
      videoWrapper.style.alignItems = 'center'
      videoWrapper.style.justifyContent = 'center'

      const videoElement = document.createElement('video')
      videoElement.setAttribute('data-user-uid', String(user.uid))
      videoElement.setAttribute('data-track-type', 'remote')
      videoElement.style.width = '100%'
      videoElement.style.height = '100%'
      videoElement.style.objectFit = 'cover'
      videoElement.style.objectPosition = 'center'
      videoElement.style.display = 'block'

      videoWrapper.appendChild(videoElement)
      gridContainer.appendChild(videoWrapper)

      if (user.videoTrack) {
        user.videoTrack.play(videoElement)

        const applyRemoteFit = () => {
          const w = videoElement.videoWidth
          const h = videoElement.videoHeight
          if (!w || !h) return
          const isPortrait = h > w
          const fit = isPortrait ? 'contain' : 'cover'
          videoElement.style.setProperty('object-fit', fit, 'important')
          videoElement.style.setProperty(
            'object-position',
            'center',
            'important'
          )
        }

        applyRemoteFit()
        videoElement.addEventListener('loadedmetadata', applyRemoteFit, {
          once: true,
        })

        const anyVideo = videoElement as any
        if (typeof anyVideo.requestVideoFrameCallback === 'function') {
          let lastW = 0
          let lastH = 0
          const tick = () => {
            if (!videoElement.isConnected) return
            const cw = videoElement.videoWidth
            const ch = videoElement.videoHeight
            if (cw && ch && (cw !== lastW || ch !== lastH)) {
              lastW = cw
              lastH = ch
              applyRemoteFit()
            }
            anyVideo.requestVideoFrameCallback(tick)
          }
          anyVideo.requestVideoFrameCallback(tick)
        }
      }
    })

    usersWithPausedVideo.forEach((user) => {
      const placeholderWrapper = document.createElement('div')
      placeholderWrapper.className = 'video-wrapper'
      placeholderWrapper.setAttribute('data-user-uid', String(user.uid))
      placeholderWrapper.setAttribute('data-placeholder', 'true')
      placeholderWrapper.style.width = '100%'
      placeholderWrapper.style.height = '100%'
      placeholderWrapper.style.position = 'relative'
      placeholderWrapper.style.backgroundColor =
        'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
      placeholderWrapper.style.background =
        'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
      placeholderWrapper.style.borderRadius = '8px'
      placeholderWrapper.style.overflow = 'hidden'
      placeholderWrapper.style.display = 'flex'
      placeholderWrapper.style.alignItems = 'center'
      placeholderWrapper.style.justifyContent = 'center'
      placeholderWrapper.style.flexDirection = 'column'
      placeholderWrapper.style.gap = '12px'

      const contentDiv = document.createElement('div')
      contentDiv.style.display = 'flex'
      contentDiv.style.flexDirection = 'column'
      contentDiv.style.alignItems = 'center'
      contentDiv.style.justifyContent = 'center'
      contentDiv.style.gap = '12px'

      const iconSvg = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg'
      )
      iconSvg.setAttribute('width', '60')
      iconSvg.setAttribute('height', '60')
      iconSvg.setAttribute('viewBox', '0 0 24 24')
      iconSvg.setAttribute('fill', 'none')
      iconSvg.style.color = '#EA7330'
      iconSvg.style.opacity = '0.6'

      const path1 = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'path'
      )
      path1.setAttribute(
        'd',
        'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
      )
      path1.setAttribute('stroke', 'currentColor')
      path1.setAttribute('stroke-width', '2')
      path1.setAttribute('stroke-linecap', 'round')
      path1.setAttribute('stroke-linejoin', 'round')

      const path2 = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'path'
      )
      path2.setAttribute('d', 'M3 3l18 18')
      path2.setAttribute('stroke', 'currentColor')
      path2.setAttribute('stroke-width', '2')
      path2.setAttribute('stroke-linecap', 'round')

      iconSvg.appendChild(path1)
      iconSvg.appendChild(path2)

      const text = document.createElement('span')
      text.textContent = 'Câmera desativada'
      text.style.color = '#FFFFFF'
      text.style.fontSize = '14px'
      text.style.fontWeight = '500'
      text.style.textAlign = 'center'
      text.style.opacity = '0.8'

      contentDiv.appendChild(iconSvg)
      contentDiv.appendChild(text)
      placeholderWrapper.appendChild(contentDiv)
      gridContainer.appendChild(placeholderWrapper)
    })
  }

  const initAgora = async () => {
    if (initInFlightRef.current) return
    initInFlightRef.current = true

    const syncExistingRemoteUsers = async () => {
      try {
        if (!agoraClient || agoraClient.connectionState !== 'CONNECTED') return

        const users = agoraClient.remoteUsers
        if (!users || users.length === 0) return

        const newRemoteUsers = new Map(remoteUsers)

        for (const user of users) {
          try {
            await agoraClient.subscribe(user, 'video')
          } catch {
            // ignore subscribe errors
          }
          try {
            await agoraClient.subscribe(user, 'audio')
          } catch {
            // ignore subscribe errors
          }

          const remoteVideoTrack = user.videoTrack
          const remoteAudioTrack = user.audioTrack

          newRemoteUsers.set(user.uid, {
            uid: user.uid,
            videoTrack: remoteVideoTrack,
            audioTrack: remoteAudioTrack,
            videoPaused: !remoteVideoTrack,
            hasVideo: !!remoteVideoTrack,
            hasAudio: !!remoteAudioTrack,
          })

          if (remoteAudioTrack) {
            try {
              remoteAudioTrack.play()
            } catch {
              // ignore
            }
          }
        }

        setRemoteUsers(newRemoteUsers)

        const hasAnyVideo = Array.from(newRemoteUsers.values()).some(
          (u) => u.hasVideo && !u.videoPaused
        )
        setRemotePaused(!hasAnyVideo)
        setRemoteDisconnected(false)

        setTimeout(() => {
          renderRemoteVideos(newRemoteUsers)
        }, 100)
      } catch (e) {
        console.warn('[useVideoCall] Falha ao sincronizar usuários remotos:', e)
      }
    }

    try {
      agoraClient.removeAllListeners('user-published')
      agoraClient.removeAllListeners('user-unpublished')
      agoraClient.removeAllListeners('user-left')
      agoraClient.removeAllListeners('connection-state-change')
      agoraClient.removeAllListeners('network-quality')
      agoraClient.removeAllListeners('token-privilege-will-expire')
      agoraClient.removeAllListeners('token-privilege-did-expire')
    } finally {
      // continua
    }

    if (agoraClient.connectionState === 'CONNECTED') {
      const users = agoraClient.remoteUsers

      if (users.length) {
        setRemoteDisconnected(false)
        const newRemoteUsers = new Map(remoteUsers)

        for (const user of users) {
          await agoraClient.subscribe(user, 'video')
          await agoraClient.subscribe(user, 'audio')

          const remoteVideoTrack = user.videoTrack
          const remoteAudioTrack = user.audioTrack

          newRemoteUsers.set(user.uid, {
            uid: user.uid,
            videoTrack: remoteVideoTrack,
            audioTrack: remoteAudioTrack,
            videoPaused: !remoteVideoTrack,
            hasVideo: !!remoteVideoTrack,
            hasAudio: !!remoteAudioTrack,
          })

          if (remoteAudioTrack) {
            remoteAudioTrack.play()
          }
        }

        setRemoteUsers(newRemoteUsers)

        const hasAnyVideo = Array.from(newRemoteUsers.values()).some(
          (u) => u.hasVideo && !u.videoPaused
        )
        setRemotePaused(!hasAnyVideo)
        setRemoteDisconnected(false)

        setTimeout(() => {
          renderRemoteVideos(newRemoteUsers)
        }, 100)
        return
      }
    }

    agoraClient.on('connection-state-change', (curState) => {
      if (curState === 'CONNECTED') {
        void syncExistingRemoteUsers()
      }
    })

    agoraClient.on('user-published', async (user, mediaType) => {
      try {
        setRemoteDisconnected(false)
        await agoraClient.subscribe(user, mediaType)

        const remoteAudioTrack = user.audioTrack
        const remoteVideoTrack = user.videoTrack

        setRemoteUsers((prev) => {
          const newMap = new Map(prev)
          const existingUser = newMap.get(user.uid)

          newMap.set(user.uid, {
            uid: user.uid,
            videoTrack: remoteVideoTrack || existingUser?.videoTrack,
            audioTrack: remoteAudioTrack || existingUser?.audioTrack,
            videoPaused: !remoteVideoTrack,
            hasVideo: !!remoteVideoTrack,
            hasAudio: !!remoteAudioTrack,
          })

          const hasAnyVideo = Array.from(newMap.values()).some(
            (u) => u.hasVideo && !u.videoPaused
          )
          setRemotePaused(!hasAnyVideo)
          setRemoteDisconnected(false)

          setTimeout(() => {
            renderRemoteVideos(newMap)
          }, 100)

          return newMap
        })

        if (remoteAudioTrack) {
          remoteAudioTrack.play()
        }
      } catch (error) {
        console.error('Erro ao subscrever usuário:', error)
      }
    })

    agoraClient.on('user-unpublished', (user, mediaType) => {
      if (mediaType === 'video') {
        setRemoteUsers((prev) => {
          const newMap = new Map(prev)
          const existingUser = newMap.get(user.uid)
          if (existingUser) {
            newMap.set(user.uid, {
              ...existingUser,
              videoPaused: true,
              hasVideo: false,
              videoTrack: null,
            })
          }

          const hasAnyVideo = Array.from(newMap.values()).some(
            (u) => u.hasVideo && !u.videoPaused
          )
          setRemotePaused(!hasAnyVideo)
          return newMap
        })

        const remote = document.getElementById('remote')
        if (remote) {
          const videoWrapper = remote.querySelector(
            `.video-wrapper[data-user-uid="${user.uid}"]`
          )
          if (videoWrapper) {
            const videoElement = videoWrapper.querySelector('video')
            if (videoElement instanceof HTMLVideoElement) {
              videoElement.pause()
              videoElement.srcObject = null
            }
            videoWrapper.remove()
          }
        }
      }
    })

    agoraClient.on('user-left', (user) => {
      setRemoteUsers((prev) => {
        const newMap = new Map(prev)

        const remote = document.getElementById('remote')
        if (remote) {
          const videoWrapper = remote.querySelector(
            `.video-wrapper[data-user-uid="${user.uid}"]`
          )
          if (videoWrapper) {
            const videoElement = videoWrapper.querySelector('video')
            if (videoElement instanceof HTMLVideoElement) {
              videoElement.pause()
              videoElement.srcObject = null
            }
            videoWrapper.remove()
          }
        }

        newMap.delete(user.uid)

        if (newMap.size === 0) {
          setRemoteDisconnected(true)
          setRemotePaused(false)
        } else {
          setTimeout(() => {
            renderRemoteVideos(newMap)
          }, 100)
        }

        return newMap
      })
    })

    agoraClient.on(
      'network-quality',
      (stats: {
        uplinkNetworkQuality?: number
        downlinkNetworkQuality?: number
      }) => {
        const txQuality = stats.uplinkNetworkQuality ?? 0
        const rxQuality = stats.downlinkNetworkQuality ?? 0
        const overallQuality = Math.round((txQuality + rxQuality) / 2)

        const browserQuality = getBrowserNetworkQuality()

        const currentQuality: NetworkQuality = {
          txQuality,
          rxQuality,
          overallQuality,
          timestamp: Date.now(),
          browserQuality,
        }

        setConnectionHistory((prev) => {
          const newHistory = [...prev, currentQuality].slice(-10)
          return newHistory
        })

        setNetworkQuality(currentQuality)

        const isOnline = checkInternetConnection()

        if (!isOnline) {
          setConnectionStatus('disconnected')
          setNetworkMessage('Sem conexão com a internet')
          setIsMonitoring(false)
          return
        }

        const status = combineNetworkQuality(
          { txQuality, rxQuality, overallQuality },
          browserQuality
        )

        let message: string
        switch (status) {
          case 'disconnected':
            message = 'Sem conexão'
            break
          case 'critical':
            message = 'Conexão muito ruim'
            break
          case 'poor':
            message = 'Conexão instável'
            break
          case 'good':
            message = 'Conexão boa'
            break
          case 'excellent':
            message = 'Conectado'
            break
          default:
            message = 'Conectado'
        }

        setConnectionStatus(status)
        setNetworkMessage(message)

        if (autoQualityEnabled && localParameters.videoTrack) {
          if (txQuality >= 4 || rxQuality >= 4) {
            if (localParameters.videoEnabled) {
              console.warn(
                'Conexão muito ruim, pausando vídeo automaticamente para manter qualidade do áudio'
              )
              localParameters.videoTrack.setEnabled(false)
              setLocalPaused(true)
              setWasVideoAutoPaused(true)
              setLocalParameters((prev) => ({
                ...prev,
                videoEnabled: false,
              }))
            }
          } else {
            if (
              !localParameters.videoEnabled &&
              localParameters.videoTrack &&
              wasVideoAutoPaused
            ) {
              if (
                txQuality < 4 &&
                rxQuality < 4 &&
                (txQuality <= 3 || rxQuality <= 3)
              ) {
                console.warn(
                  'Conexão melhorou, reativando vídeo automaticamente'
                )
                localParameters.videoTrack.setEnabled(true)
                setLocalPaused(false)
                setWasVideoAutoPaused(false)
                setLocalParameters((prev) => ({
                  ...prev,
                  videoEnabled: true,
                }))
              }
            }

            adjustQualityBasedOnNetwork(txQuality, rxQuality)
          }
        }

        setConnectionHistory((prev) => {
          if (prev.length > 1) {
            const previousQuality = prev[prev.length - 2]
            const qualityDrop = previousQuality.overallQuality - overallQuality

            if (qualityDrop >= 2) {
              console.warn(
                `Queda súbita na qualidade da conexão: ${qualityDrop} pontos`
              )
            }
          }
          return prev
        })
      }
    )

    agoraClient.on('connection-state-change', (curState) => {
      const isOnline = checkInternetConnection()

      if (curState === 'CONNECTED') {
        if (!isOnline) {
          setConnectionStatus('disconnected')
          setNetworkMessage('Sem conexão com a internet')
          setIsMonitoring(false)
          setRemoteDisconnected(true)
          return
        }
        setIsMonitoring(true)
        setConnectionStatus('good')
        setNetworkMessage('Conectado')
        setRemoteDisconnected(false)
      } else if (curState === 'DISCONNECTED') {
        setIsMonitoring(false)
        setConnectionStatus('disconnected')
        setNetworkMessage(
          isOnline ? 'Conexão perdida' : 'Sem conexão com a internet'
        )
        setRemoteDisconnected(true)
      } else if (curState === 'CONNECTING') {
        if (!isOnline) {
          setConnectionStatus('disconnected')
          setNetworkMessage('Sem conexão com a internet')
        } else {
          setNetworkMessage('Conectando...')
        }
      } else if (curState === 'RECONNECTING') {
        if (!isOnline) {
          setConnectionStatus('disconnected')
          setNetworkMessage('Sem conexão com a internet')
        } else {
          setNetworkMessage('Reconectando...')
        }
      }
    })

    agoraClient.on('token-privilege-will-expire', () => {
      console.warn('Token está prestes a expirar')
      setNetworkMessage('Token expirando em breve')
    })

    agoraClient.on('token-privilege-did-expire', () => {
      console.error('Token expirou')
      setConnectionStatus('disconnected')
      setNetworkMessage('Sessão expirada - reconecte')
    })

    initInFlightRef.current = false
  }

  const join = async (schedulingId: string, token: string) => {
    if (!agoraClient) return

    if (joinInFlightRef.current) {
      console.warn('Já está tentando conectar (ref), aguardando...')
      return
    }
    joinInFlightRef.current = true
    setIsJoining(true)

    try {
      await initAgora()

      if (agoraClient.connectionState === 'CONNECTED') {
        console.warn(
          'Já está conectado ao canal, saindo primeiro para evitar conflito (Fast Refresh)...'
        )
        try {
          if (localParameters.audioTrack) {
            try {
              const audioTrack = localParameters.audioTrack
              try {
                await agoraClient.unpublish(audioTrack)
              } catch (e) {
                console.warn('Erro ao unpublish audio track:', e)
              }
              try {
                const mediaStreamTrack = audioTrack.getMediaStreamTrack()
                if (
                  mediaStreamTrack &&
                  mediaStreamTrack.readyState !== 'ended'
                ) {
                  mediaStreamTrack.stop()
                }
              } catch (e) {
                console.warn('Erro ao parar MediaStreamTrack de áudio:', e)
              }
              audioTrack.close()
            } catch (e) {
              console.warn('Erro ao fechar audio track:', e)
            }
          }
          if (localParameters.videoTrack) {
            try {
              await agoraClient.unpublish(localParameters.videoTrack)
              localParameters.videoTrack.close()
            } catch (e) {
              console.warn('Erro ao fechar video track:', e)
            }
          }
          await agoraClient.leave()
          await new Promise((resolve) => setTimeout(resolve, 500))
        } catch (leaveError) {
          console.warn('Erro ao sair do canal antes de reconectar:', leaveError)
        }
      }

      if (agoraClient.connectionState === 'CONNECTING') {
        console.warn('Aguardando conexão anterior terminar...')
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            agoraClient.off('connection-state-change', checkState)
            setIsJoining(false)
            reject(new Error('Timeout aguardando conexão anterior'))
          }, 5000)

          const checkState = (curState: string) => {
            if (curState === 'CONNECTED' || curState === 'DISCONNECTED') {
              clearTimeout(timeout)
              agoraClient.off('connection-state-change', checkState)
              resolve()
            }
          }

          agoraClient.on('connection-state-change', checkState)
        })
      }

      if (!token) {
        console.error('Token não fornecido')
        return
      }

      const appId = process.env.REACT_APP_AGORA_IO_APP_ID
      if (!appId) {
        console.error('App ID não fornecido')
        setIsJoining(false)
        return
      }

      await agoraClient.join(appId, schedulingId, token, null)

      const waitForConnection = (): Promise<void> => {
        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            agoraClient.off('connection-state-change', checkState)
            reject(new Error('Timeout ao aguardar conexão'))
          }, 5000)

          const checkState = (curState: string) => {
            if (curState === 'CONNECTED') {
              clearTimeout(timeout)
              agoraClient.off('connection-state-change', checkState)
              resolve()
            } else if (curState === 'DISCONNECTED') {
              clearTimeout(timeout)
              agoraClient.off('connection-state-change', checkState)
              reject(new Error(`Falha ao conectar: ${curState}`))
            }
          }

          const currentState = agoraClient.connectionState as string
          if (currentState === 'CONNECTED') {
            clearTimeout(timeout)
            resolve()
            return
          }

          agoraClient.on('connection-state-change', checkState)
        })
      }

      await waitForConnection()

      setRemoteDisconnected(false)
      void (async () => {
        try {
          const users = agoraClient.remoteUsers
          if (!users || users.length === 0) return

          const newRemoteUsers = new Map(remoteUsers)
          for (const user of users) {
            try {
              await agoraClient.subscribe(user, 'video')
            } catch (e) {
              console.warn(
                '[useVideoCall] falha ao subscribe vídeo (sync pós-join):',
                e
              )
            }
            try {
              await agoraClient.subscribe(user, 'audio')
            } catch (e) {
              console.warn(
                '[useVideoCall] falha ao subscribe áudio (sync pós-join):',
                e
              )
            }

            const remoteVideoTrack = user.videoTrack
            const remoteAudioTrack = user.audioTrack

            newRemoteUsers.set(user.uid, {
              uid: user.uid,
              videoTrack: remoteVideoTrack,
              audioTrack: remoteAudioTrack,
              videoPaused: !remoteVideoTrack,
              hasVideo: !!remoteVideoTrack,
              hasAudio: !!remoteAudioTrack,
            })

            if (remoteAudioTrack) {
              try {
                remoteAudioTrack.play()
              } catch (e) {
                console.warn(
                  '[useVideoCall] falha ao tocar áudio remoto (sync pós-join):',
                  e
                )
              }
            }
          }

          setRemoteUsers(newRemoteUsers)
          const hasAnyVideo = Array.from(newRemoteUsers.values()).some(
            (u) => u.hasVideo && !u.videoPaused
          )
          setRemotePaused(!hasAnyVideo)
          setRemoteDisconnected(false)
          setTimeout(() => renderRemoteVideos(newRemoteUsers), 100)
        } catch (e) {
          console.warn('[useVideoCall] sync pós-join falhou:', e)
        }
      })()

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        })
        stream.getTracks().forEach((track) => track.stop())
      } catch (permissionError: any) {
        console.warn('Erro ao verificar permissões:', permissionError)

        let localAudioTrack: IMicrophoneAudioTrack | null = null
        let localVideoTrack: ICameraVideoTrack | null = null

        try {
          localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack()
        } catch (audioError: any) {
          console.error('Erro ao criar track de áudio:', audioError)
          if (
            audioError.name === 'NotAllowedError' ||
            audioError.name === 'PermissionDeniedError'
          ) {
            throw new Error(
              'Permissão de microfone negada. Por favor, permita o acesso ao microfone nas configurações do navegador.'
            )
          }
          throw audioError
        }

        try {
          localVideoTrack = await AgoraRTC.createCameraVideoTrack()
        } catch (videoError: any) {
          console.error('Erro ao criar track de vídeo:', videoError)
          if (
            videoError.name === 'NotAllowedError' ||
            videoError.name === 'PermissionDeniedError'
          ) {
            if (localAudioTrack) {
              console.warn('Continuando apenas com áudio, vídeo negado')
              setLocalParameters({
                audioTrack: localAudioTrack,
                videoTrack: null,
                audioMuted: false,
                videoEnabled: false,
              })
              await agoraClient.publish([localAudioTrack])
              return
            }
            throw new Error(
              'Permissão de câmera negada. Por favor, permita o acesso à câmera nas configurações do navegador.'
            )
          }
          throw videoError
        }

        const local = document.getElementById('local')
        setLocalParameters({
          audioTrack: localAudioTrack,
          videoTrack: localVideoTrack,
          audioMuted: false,
          videoEnabled: true,
        })

        if (local && localVideoTrack) {
          const existingVideos = local.querySelectorAll('video')
          existingVideos.forEach((video) => {
            if (video instanceof HTMLVideoElement) {
              video.pause()
              video.srcObject = null
            }
            video.remove()
          })

          const videoElement = document.createElement('video')
          videoElement.setAttribute('data-track-type', 'local')
          videoElement.style.width = '100%'
          videoElement.style.height = '100%'
          videoElement.style.objectFit = 'cover'
          videoElement.style.display = 'block'
          local.appendChild(videoElement)

          setTimeout(() => {
            try {
              localVideoTrack.play(videoElement)
            } catch (error) {
              console.error(
                '[useVideoCall] Erro ao reproduzir vídeo local:',
                error
              )
              try {
                localVideoTrack.play(local)
              } catch (fallbackError) {
                console.error(
                  '[useVideoCall] Erro ao reproduzir no container:',
                  fallbackError
                )
              }
            }
          }, 50)
        }

        const tracksToPublish: (IMicrophoneAudioTrack | ICameraVideoTrack)[] = [
          localAudioTrack,
        ]
        if (localVideoTrack) {
          tracksToPublish.push(localVideoTrack)
        }

        const connectionState = agoraClient.connectionState as string
        if (connectionState === 'CONNECTED') {
          await agoraClient.publish(tracksToPublish)
        } else {
          throw new Error(
            'Não foi possível publicar: não está conectado ao canal'
          )
        }

        if (localVideoTrack) {
          const bgProcessor = extension.createProcessor()
          await bgProcessor.init()
          localVideoTrack
            .pipe(bgProcessor)
            .pipe(localVideoTrack.processorDestination)
          setProcessor(bgProcessor)
        }
        return
      }

      const initialQuality: VideoQuality = 'high'
      const qualityConfig = VIDEO_QUALITY_CONFIGS[initialQuality]

      const [localAudioTrack, localVideoTrack] =
        await AgoraRTC.createMicrophoneAndCameraTracks(
          {
            encoderConfig: 'music_standard',
          },
          {
            encoderConfig: {
              width: qualityConfig.width,
              height: qualityConfig.height,
              frameRate: qualityConfig.frameRate,
              bitrateMax: qualityConfig.bitrateMax,
              bitrateMin: qualityConfig.bitrateMin,
            },
            cameraId: selectedCamera || undefined,
          }
        )

      const local = document.getElementById('local')
      setLocalParameters({
        audioTrack: localAudioTrack,
        videoTrack: localVideoTrack,
        audioMuted: false,
        videoEnabled: true,
      })

      const bgProcessor = extension.createProcessor()
      await bgProcessor.init()
      bgProcessor.setOptions({
        type: 'none',
      })

      localVideoTrack
        .pipe(bgProcessor)
        .pipe(localVideoTrack.processorDestination)
      setProcessor(bgProcessor)

      if (local) {
        const existingVideos = local.querySelectorAll('video')
        existingVideos.forEach((video) => {
          if (video instanceof HTMLVideoElement) {
            video.pause()
            video.srcObject = null
          }
          video.remove()
        })

        const remote = document.getElementById('remote')
        if (remote) {
          const remoteVideosInLocal = Array.from(existingVideos).filter(
            (video) => {
              return video.getAttribute('data-track-type') === 'remote'
            }
          )
          remoteVideosInLocal.forEach((video) => {
            video.remove()
          })
        }

        const videoElement = document.createElement('video')
        videoElement.setAttribute('data-track-type', 'local')
        videoElement.style.width = '100%'
        videoElement.style.height = '100%'
        videoElement.style.objectFit = 'cover'
        videoElement.style.display = 'block'
        local.appendChild(videoElement)

        setTimeout(() => {
          try {
            localVideoTrack.play(videoElement)
          } catch (error) {
            console.error(
              '[useVideoCall] Erro ao reproduzir vídeo local:',
              error
            )
            try {
              localVideoTrack.play(local)
            } catch (fallbackError) {
              console.error(
                '[useVideoCall] Erro ao reproduzir no container:',
                fallbackError
              )
            }
          }
        }, 50)
      }

      const connectionState = agoraClient.connectionState as string
      if (connectionState === 'CONNECTED') {
        await agoraClient.publish([localAudioTrack, localVideoTrack])
        console.warn('Tracks publicados com sucesso')
      } else {
        throw new Error(
          'Não foi possível publicar: não está conectado ao canal'
        )
      }
    } catch (e: any) {
      console.error('Erro ao entrar na chamada:', e)

      if (e.message && e.message.includes('Permissão')) {
        throw e
      }

      if (
        e.name === 'NotAllowedError' ||
        e.name === 'PermissionDeniedError' ||
        e.code === 'PERMISSION_DENIED' ||
        e.message?.includes('PERMISSION_DENIED')
      ) {
        throw new Error(
          'Permissão de câmera/microfone negada. Por favor, permita o acesso nas configurações do navegador.'
        )
      }

      throw e
    } finally {
      setIsJoining(false)
      joinInFlightRef.current = false
    }
  }

  const leaveRoom = async () => {
    if (agoraClient) {
      try {
        const canUnpublish = agoraClient.connectionState === 'CONNECTED'

        if (localParameters) {
          if (localParameters.audioTrack) {
            const audioTrack = localParameters.audioTrack
            try {
              if (canUnpublish) {
                await agoraClient.unpublish(audioTrack)
              }
            } catch (e) {
              console.warn('Erro ao unpublish audio track:', e)
            }
            try {
              try {
                const mediaStreamTrack = audioTrack.getMediaStreamTrack()
                if (
                  mediaStreamTrack &&
                  mediaStreamTrack.readyState !== 'ended'
                ) {
                  mediaStreamTrack.stop()
                }
              } catch (trackError) {
                console.warn(
                  'Erro ao parar MediaStreamTrack de áudio:',
                  trackError
                )
              }
            } finally {
              try {
                audioTrack.close()
              } catch (closeError) {
                console.error('Erro ao fechar audio track:', closeError)
              }
            }
          }
          if (localParameters.videoTrack) {
            const videoTrack = localParameters.videoTrack

            try {
              if (canUnpublish) {
                await agoraClient.unpublish(videoTrack)
              }
            } catch (e) {
              console.warn('Erro ao unpublish video track:', e)
            }

            try {
              if (videoTrack.isPlaying) {
                try {
                  videoTrack.stop()
                } catch (stopError) {
                  console.warn('Erro ao parar reprodução do vídeo:', stopError)
                }
              }
            } catch {
              // ignore
            }

            try {
              const mediaStreamTrack = videoTrack.getMediaStreamTrack()
              if (mediaStreamTrack && mediaStreamTrack.readyState !== 'ended') {
                mediaStreamTrack.stop()
              }
            } catch (trackError) {
              console.warn(
                'Erro ao obter/parar MediaStreamTrack de vídeo:',
                trackError
              )
            } finally {
              try {
                videoTrack.close()
              } catch (closeError) {
                console.error('Erro ao fechar video track:', closeError)
              }
            }
          }
        }

        if (processor) {
          try {
            await processor.disable()
          } catch (e) {
            console.error('Erro ao desabilitar processor:', e)
          }
        }

        try {
          if (agoraClient.connectionState !== 'DISCONNECTED') {
            await agoraClient.leave()
          }
        } catch (leaveError) {
          console.warn('Erro ao sair do canal (leave):', leaveError)
        }

        agoraClient.removeAllListeners()

        setLocalParameters({
          audioTrack: null,
          videoTrack: null,
          audioMuted: false,
          videoEnabled: false,
        })
        setProcessor(null)
        setRemoteDisconnected(true)
        setRemotePaused(false)
        setRemoteUsers(new Map())
        setIsMonitoring(false)
        setIsJoining(false)

        const remote = document.getElementById('remote')
        if (remote) {
          const remoteVideos = remote.querySelectorAll('video[data-user-uid]')
          remoteVideos.forEach((video) => {
            if (video instanceof HTMLVideoElement) {
              video.pause()
              video.srcObject = null
            }
            video.remove()
          })
          const gridContainer = remote.querySelector('.remote-videos-grid')
          if (gridContainer) {
            gridContainer.remove()
          }
        }

        const local = document.getElementById('local')
        if (local) {
          const localVideos = local.querySelectorAll('video')
          localVideos.forEach((video) => {
            if (video instanceof HTMLVideoElement) {
              if (video.srcObject) {
                const stream = video.srcObject as MediaStream
                stream.getTracks().forEach((track) => {
                  track.stop()
                })
                video.srcObject = null
              }
              video.pause()
              video.remove()
            }
          })
        }
        try {
          const allVideos = document.querySelectorAll('video')
          allVideos.forEach((video) => {
            if (video instanceof HTMLVideoElement && video.srcObject) {
              const stream = video.srcObject as MediaStream
              stream.getTracks().forEach((track) => {
                if (track.kind === 'video' && track.readyState !== 'ended') {
                  track.stop()
                }
              })
            }
          })
        } catch (enumError) {
          console.warn('Erro ao enumerar dispositivos:', enumError)
        }

        removeLocalStorage('@starbem:selectedAppointmentDetails')
      } catch (error) {
        console.error('Erro ao sair da sala:', error)
      }
    }
  }

  const getDevices = async () => {
    const allDevices = await AgoraRTC.getDevices()
    const cameras = allDevices.filter((device) => device.kind === 'videoinput')
    const microphones = allDevices.filter(
      (device) => device.kind === 'audioinput'
    )
    setDevices({ cameras, microphones })
    setSelectedCamera(cameras[0]?.deviceId || '')
    setSelectedMicrophone(microphones[0]?.deviceId || '')
  }

  const getProcessorInstance = async () => {
    if (!processor && localParameters.videoTrack) {
      const newProcessor = extension.createProcessor()
      try {
        await newProcessor.init()
        localParameters.videoTrack
          .pipe(newProcessor)
          .pipe(localParameters.videoTrack.processorDestination)
        setProcessor(newProcessor)
        return newProcessor
      } catch (error) {
        console.error(error)
        return null
      }
    }
    return processor
  }

  const setBackgroundNone = async () => {
    const proc = await getProcessorInstance()
    if (proc) {
      await proc.disable()
      setSelectedBg('none')
    }
  }

  const setBackgroundBlurring = async () => {
    const proc = await getProcessorInstance()
    if (proc) {
      proc.setOptions({ type: 'blur', blurDegree: 2 })
      await proc.enable()
      setSelectedBg('blur')
    }
  }

  const setBackgroundImage = async (imageUrl: string) => {
    const imgElement = document.createElement('img')
    imgElement.onload = async () => {
      const proc = await getProcessorInstance()
      if (proc) {
        const options = { type: 'img', source: imgElement }
        try {
          await proc.setOptions(options)
          await proc.enable()
          setSelectedBg(imageUrl)
        } catch (e) {
          console.error('Erro ao definir imagem de fundo:', e)
        }
      }
    }
    imgElement.src = imageUrl
  }

  const toggleAudio = () => {
    const audioMuted = !localParameters.audioMuted
    localParameters?.audioTrack?.setMuted(audioMuted)
    setLocalParameters({ ...localParameters, audioMuted })
  }

  const toggleVideo = () => {
    if (!localParameters.videoTrack) {
      console.warn(
        'Tentativa de ativar vídeo, mas o track de vídeo não está disponível'
      )
      return
    }
    const videoEnabled = !localParameters.videoEnabled
    localParameters.videoTrack.setEnabled(videoEnabled)
    setLocalPaused(!videoEnabled)

    if (videoEnabled) {
      setWasVideoAutoPaused(false)
    }
    setLocalParameters({ ...localParameters, videoEnabled })
  }

  const handleVideoClick = () => {
    setIsLocalMain(!isLocalMain)
  }

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen)
  }

  const handleChangeCamera = async (deviceId: string) => {
    if (agoraClient && localParameters.videoTrack) {
      const newVideoTrack = await AgoraRTC.createCameraVideoTrack({
        cameraId: deviceId,
      })
      await agoraClient.unpublish(localParameters.videoTrack)
      localParameters.videoTrack.close()
      setLocalParameters((prev) => ({
        ...prev,
        videoTrack: newVideoTrack,
      }))
      const local = document.getElementById('local')
      if (local) {
        const existingVideos = local.querySelectorAll('video')
        existingVideos.forEach((video) => {
          video.remove()
        })

        newVideoTrack.play(local)

        setTimeout(() => {
          const videoElement = local.querySelector('video')
          if (videoElement) {
            videoElement.setAttribute('data-track-type', 'local')
          }
        }, 100)
      }
      await agoraClient.publish(newVideoTrack)
    }
  }

  const handleChangeMicrophone = async (deviceId: string) => {
    if (agoraClient && localParameters.audioTrack) {
      const newAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
        microphoneId: deviceId,
      })
      await agoraClient.unpublish(localParameters.audioTrack)
      localParameters.audioTrack.close()
      setLocalParameters((prev) => ({
        ...prev,
        audioTrack: newAudioTrack,
      }))
      await agoraClient.publish(newAudioTrack)
      setSelectedMicrophone(deviceId)
    }
  }

  const handleConfigClick = () => {
    setShowConfig(!showConfig)
    setShowDeviceConfig(false)
    setButtonsDisabled(!buttonsDisabled)
  }

  const handleDeviceConfigClick = () => {
    setShowDeviceConfig(!showDeviceConfig)
    setShowConfig(false)
    setButtonsDisabled(!buttonsDisabled)
  }

  const adjustQualityBasedOnNetwork = (uplink: number, downlink: number) => {
    if (uplink >= 4 || downlink >= 4) {
      return
    }

    const avgQuality = (uplink + downlink) / 2

    let targetQuality: VideoQuality = currentVideoQuality

    if (avgQuality >= 3) {
      targetQuality = 'low'
    } else if (avgQuality >= 2) {
      targetQuality = 'medium'
    } else if (avgQuality <= 1) {
      targetQuality = 'high'
    }

    if (targetQuality !== currentVideoQuality) {
      console.warn(
        `Ajustando qualidade de ${currentVideoQuality} para ${targetQuality} (uplink: ${uplink}, downlink: ${downlink})`
      )
      setVideoQuality(targetQuality)
    }
  }

  const setVideoQuality = async (quality: VideoQuality) => {
    if (!localParameters.videoTrack || !agoraClient) {
      return
    }

    try {
      const config = VIDEO_QUALITY_CONFIGS[quality]

      const newVideoTrack = await AgoraRTC.createCameraVideoTrack({
        encoderConfig: {
          width: config.width,
          height: config.height,
          frameRate: config.frameRate,
          bitrateMax: config.bitrateMax,
          bitrateMin: config.bitrateMin,
        },
        cameraId: selectedCamera || undefined,
      })

      if (processor) {
        newVideoTrack.pipe(processor).pipe(newVideoTrack.processorDestination)
      }

      await agoraClient.unpublish(localParameters.videoTrack)
      localParameters.videoTrack.close()

      await agoraClient.publish(newVideoTrack)

      const local = document.getElementById('local')
      if (local) {
        const existingVideos = local.querySelectorAll('video')
        existingVideos.forEach((video) => {
          video.remove()
        })

        newVideoTrack.play(local)

        setTimeout(() => {
          const videoElement = local.querySelector('video')
          if (videoElement) {
            videoElement.setAttribute('data-track-type', 'local')
          }
        }, 100)
      }

      setLocalParameters((prev) => ({
        ...prev,
        videoTrack: newVideoTrack,
      }))

      setCurrentVideoQuality(quality)
      console.warn(`Qualidade de vídeo alterada para: ${quality}`)
    } catch (error) {
      console.error('Erro ao alterar qualidade de vídeo:', error)
    }
  }

  const checkConnectionQuality = async () => {
    if (!agoraClient || agoraClient.connectionState !== 'CONNECTED') {
      return { status: 'disconnected', message: 'Não conectado' }
    }

    try {
      const stats = await agoraClient.getRTCStats()
      return {
        status: connectionStatus,
        message: networkMessage,
        stats,
        networkQuality,
        connectionHistory: connectionHistory.slice(-5),
      }
    } catch (error) {
      console.error('Erro ao verificar qualidade da conexão:', error)
      return { status: 'error', message: 'Erro na verificação' }
    }
  }

  useEffect(() => {
    if (remoteUsers.size <= 1) return

    const handleResize = () => {
      setTimeout(() => {
        renderRemoteVideos(remoteUsers)
      }, 100)
    }

    const resizeObserver = new ResizeObserver(handleResize)
    const remote = document.getElementById('remote')

    if (remote) {
      resizeObserver.observe(remote)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      if (remote) {
        resizeObserver.unobserve(remote)
      }
      window.removeEventListener('resize', handleResize)
    }
  }, [remoteUsers])

  useEffect(() => {
    if (!isMonitoring) return

    const interval = setInterval(() => {
      const isOnline = checkInternetConnection()
      const agoraState = agoraClient.connectionState

      if (!isOnline) {
        setConnectionStatus('disconnected')
        setNetworkMessage('Sem conexão com a internet')
        setIsMonitoring(false)
        return
      }

      if (agoraState !== 'CONNECTED') {
        setConnectionStatus('disconnected')
        setNetworkMessage('Conexão perdida')
        setIsMonitoring(false)
      }
    }, 2000)

    return () => clearInterval(interval)
  }, [isMonitoring, agoraClient])

  useEffect(() => {
    const handleOnline = () => {
      if (agoraClient && agoraClient.connectionState === 'CONNECTED') {
        setConnectionStatus('good')
        setNetworkMessage('Conectado')
      }
    }

    const handleOffline = () => {
      setConnectionStatus('disconnected')
      setNetworkMessage('Sem conexão com a internet')
      setIsMonitoring(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [agoraClient])

  return {
    agoraClient,
    leaveRoom,
    processor,
    remotePaused,
    remoteDisconnected,
    remoteUsers,
    initAgora,
    join,
    extension,
    localParameters,
    setLocalParameters,
    setProcessor,
    getDevices,
    devices,
    selectedCamera,
    selectedMicrophone,
    handleChangeCamera,
    handleDeviceConfigClick,
    handleConfigClick,
    handleChangeMicrophone,
    toggleFullScreen,
    handleVideoClick,
    toggleAudio,
    toggleVideo,
    setBackgroundBlurring,
    setBackgroundNone,
    setBackgroundImage,
    selectedBg,
    localPaused,
    isFullScreen,
    isLocalMain,
    showConfig,
    setShowConfig,
    showDeviceConfig,
    setShowDeviceConfig,
    buttonsDisabled,
    setButtonsDisabled,
    networkQuality,
    networkMessage,
    setNetworkMessage,
    connectionStatus,
    connectionHistory,
    isMonitoring,
    checkConnectionQuality,
    currentVideoQuality,
    setVideoQuality,
    autoQualityEnabled,
    setAutoQualityEnabled,
  }
}
